/**
 * Meta (Facebook) Pixel — typed wrapper.
 *
 * Everything the app calls goes through here rather than touching window.fbq
 * directly, so event names can't be typo'd, calls before the script loads are
 * safe no-ops, and consent is enforced in one place.
 *
 * See components/MetaPixel.tsx for loading and PageView-on-navigation.
 */

import { SITE_CONFIG } from "@/config/site";
import { isTrackingAllowed } from "./consent";

/**
 * Set NEXT_PUBLIC_META_PIXEL_ID in .env.local — never hardcode it here, so
 * staging and production can point at different pixels. Empty disables the
 * pixel completely.
 */
export const META_PIXEL_ID = SITE_CONFIG.META_PIXEL_ID;

/** False when no pixel is configured for this environment. */
export const isPixelConfigured = Boolean(META_PIXEL_ID);

/**
 * Meta's standard events. Custom strings are allowed by fbq, but sticking to
 * these is what lets Meta optimise delivery and build lookalike audiences —
 * a custom event name is just an opaque label to the ad system.
 */
export type MetaStandardEvent =
  | "AddPaymentInfo"
  | "AddToCart"
  | "AddToWishlist"
  | "CompleteRegistration"
  | "Contact"
  | "CustomizeProduct"
  | "Donate"
  | "FindLocation"
  | "InitiateCheckout"
  | "Lead"
  | "PageView"
  | "Purchase"
  | "Schedule"
  | "Search"
  | "StartTrial"
  | "SubmitApplication"
  | "Subscribe"
  | "ViewContent";

export interface MetaEventParams {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  currency?: string;
  value?: number;
  search_string?: string;
  status?: string;
  predicted_ltv?: number;
  [key: string]: unknown;
}

/**
 * Personal data used for Advanced Matching. Meta hashes these in the browser
 * before sending — we never transmit them in the clear.
 *
 * Match quality is the single biggest lever on attribution accuracy, so pass
 * whatever the user has already given you on that form.
 */
export interface MetaUserData {
  /** Email — lower-cased and trimmed before hashing. */
  em?: string;
  /** Phone — digits only, including country code, no +. */
  ph?: string;
  /** First name, lower-cased. */
  fn?: string;
  /** Last name, lower-cased. */
  ln?: string;
  /** Country as a two-letter lower-case code, e.g. "in". */
  country?: string;
}

type Fbq = ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

// Consent lives in lib/consent.ts so every tag (GA, Clarity, LinkedIn, Meta)
// reads the same rule. Re-exported here for callers already importing from this
// module.
export { CONSENT_EVENT, isTrackingAllowed } from "./consent";

/**
 * A unique id for one conversion, shared between the browser event and the
 * matching server-side Conversions API event so Meta counts it once.
 *
 * Return it from the caller and send it with the server event when CAPI lands.
 */
export function newEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Normalise personal data the way Meta expects before it hashes it. */
function normaliseUserData(user: MetaUserData): Record<string, string> {
  const out: Record<string, string> = {};
  if (user.em) out.em = user.em.trim().toLowerCase();
  // Meta wants digits only, country code included: "919876543210".
  if (user.ph) out.ph = user.ph.replace(/\D/g, "");
  if (user.fn) out.fn = user.fn.trim().toLowerCase();
  if (user.ln) out.ln = user.ln.trim().toLowerCase();
  if (user.country) out.country = user.country.trim().toLowerCase();
  return out;
}

function canTrack(): boolean {
  return (
    isPixelConfigured &&
    typeof window !== "undefined" &&
    typeof window.fbq === "function" &&
    isTrackingAllowed()
  );
}

/**
 * Track a Meta standard event.
 *
 * @returns the eventID used, so a server-side duplicate can be de-duplicated
 *          against it. Null when nothing was sent.
 */
export function trackEvent(
  event: MetaStandardEvent,
  params: MetaEventParams = {},
  user?: MetaUserData
): string | null {
  if (!canTrack()) return null;

  const eventID = newEventId();

  try {
    if (user) {
      // Re-init with advanced matching so this event and everything after it
      // carries the hashed identifiers. fbq hashes them client-side.
      window.fbq!("init", META_PIXEL_ID, normaliseUserData(user));
    }
    window.fbq!("track", event, params, { eventID });
    return eventID;
  } catch {
    // Never let analytics break a form submission.
    return null;
  }
}

/** Track an event Meta has no standard name for. Prefer trackEvent. */
export function trackCustom(name: string, params: MetaEventParams = {}): string | null {
  if (!canTrack()) return null;

  const eventID = newEventId();
  try {
    window.fbq!("trackCustom", name, params, { eventID });
    return eventID;
  } catch {
    return null;
  }
}

/** Fire a PageView. Called on first load and on every SPA navigation. */
export function trackPageView(): void {
  if (!canTrack()) return;
  try {
    window.fbq!("track", "PageView");
  } catch {
    /* no-op */
  }
}
